import { HoverSelect, type HoverSelectOption } from '@neo/primitives/hover-select'
import { Label } from '@neo/primitives/label'
import { Separator } from '@neo/primitives/separator'
import { Textarea } from '@neo/primitives/textarea'
import { X } from 'lucide-react'
import type React from 'react'
import { useMemo, useRef } from 'react'
import { isSubAccountEmbedSession } from 'v2/shared/utils/embed-session'
import { isValidUrl } from 'v2/utils'
import { ProviderTypes } from '../../config/agent-settings.validation.config'
import type { PromptItem } from '../../types'
import { NodevarList } from '../nodevar-list'
import { VoicePlayControl } from '../voice-play-control'
import { AudioUploadCard } from './audio-upload-card'
import { basicLanguageOptions, promptSpeakTypeOptions, voiceOptions } from './constants'
import SelectedLabel from './selected-label'
import { useAIVoices } from './use-ai-voices'

const staticMenuHoverCardOptions: { [key: string]: HoverSelectOption[] } = {
  prompt_speak_type: promptSpeakTypeOptions,
  language: basicLanguageOptions,
  voice: voiceOptions,
}

const labelMap = {
  prompt_speak_type: 'Speech Processor',
  language: 'Language',
  voice: 'Voice',
}

interface AudioPromptProps {
  value: PromptItem
  arrayIndex: number
  onChange: (value: PromptItem) => void
  nodeVars?: string[]
  label?: string
  separatorPosition?: 'below' | 'above'
  showAIVoices?: boolean
}

export const AudioPrompt: React.FC<AudioPromptProps> = ({
  value,
  arrayIndex,
  onChange,
  nodeVars,
  label,
  separatorPosition,
  showAIVoices = true,
}) => {
  const { apiLanguageOptions, voiceOptions, handleAIVoiceChange } = useAIVoices(showAIVoices, value)
  // AI voices come from voice providers, which hodor refuses to sub-account embed sessions; the
  // option stays only so an existing selection still renders.
  const offerAIVoices =
    showAIVoices &&
    (!isSubAccountEmbedSession() || value.prompt_speak_type === ProviderTypes.ElevenLabs)

  const handleOptionChange = (type: string, v: string) => {
    const updatedValue = handleAIVoiceChange(type, v, value)
    onChange(updatedValue)
  }

  const handleUpload = (url: string, name: string) => {
    onChange({ ...value, prompt_type: 'play', url, file: name })
  }

  const isValidURL = isValidUrl(value.url)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const promptIndex = (arrayIndex ?? false) ? arrayIndex + 1 : ''
  const shouldShowTextUI =
    (value.prompt_speak_type === ProviderTypes.ElevenLabs &&
      value.text &&
      value.text.trim().length > 0) ||
    !isValidURL

  const transformedVoiceOptions = useMemo(() => {
    if (value.prompt_speak_type !== ProviderTypes.ElevenLabs) {
      return staticMenuHoverCardOptions.voice
    }

    return voiceOptions.map((option) => ({
      ...option,
      label: (
        <div className="flex items-center gap-2 w-full">
          <VoicePlayControl
            voice={option.voice}
            sttTtsConfig={option.sttTtsConfig}
            isPlaying={option.audioHandlers.isPlaying}
            loadingVoiceId={option.audioHandlers.loadingVoiceId}
            onPlayPreview={(voiceId, previewUrl, model, provider, languageCode) =>
              option.audioHandlers.handlePlayPreview({
                voiceId,
                previewUrl,
                model: model || option.sttTtsConfig.ttsModel,
                provider: provider || option.sttTtsConfig.ttsProvider,
                languageCode,
              })
            }
            onStop={option.audioHandlers.stop}
            options={{ showLabel: false, className: 'w-4 h-4' }}
          />
          <div className="flex min-w-0 flex-1">
            <div className="truncate text-p-400">{option.label}</div>
          </div>
        </div>
      ),
      selectedLabel: <span className="text-p-400">{option.selectedLabel}</span>,
    }))
  }, [voiceOptions, value.prompt_speak_type])

  const transformedLanguageOptions = useMemo(() => {
    if (value.prompt_speak_type === ProviderTypes.ElevenLabs) {
      return apiLanguageOptions.map((option) => ({
        ...option,
        selectedLabel: <SelectedLabel text={option.selectedLabel} className="justify-center" />,
      }))
    }
    return staticMenuHoverCardOptions.language
  }, [apiLanguageOptions, value.prompt_speak_type])

  const menuHoverCardOptions = useMemo(
    () => ({
      prompt_speak_type: offerAIVoices
        ? staticMenuHoverCardOptions.prompt_speak_type
        : staticMenuHoverCardOptions.prompt_speak_type.filter(
            (opt) => opt.value !== ProviderTypes.ElevenLabs,
          ),
      language: transformedLanguageOptions,
      voice: transformedVoiceOptions,
    }),
    [transformedLanguageOptions, transformedVoiceOptions, offerAIVoices],
  )

  return (
    <>
      {separatorPosition === 'above' && (
        <Separator orientation="horizontal" className="h-px w-full mb-4 bg-bg1" />
      )}
      <div className="relative">
        <Label>{label || `Prompt ${promptIndex}`}</Label>
        {shouldShowTextUI ? (
          <div className="group focus-within:ring-2 focus-within:ring-ring rounded-md">
            <Textarea
              id={`audio-prompt-${promptIndex}`}
              ref={textareaRef}
              className="scroll-m-8 rounded-b-none border-b-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
              placeholder="Enter text here or attach an audio file"
              rows={4}
              value={value.text}
              onChange={(e) => handleOptionChange('text', e.target.value)}
            />
            <div className="flex items-center justify-end px-2 py-2 gap-2 bg-background border border-t-0 rounded-b-md">
              <AudioUploadCard url={value.url} onURLChange={handleUpload} />
              <Separator orientation="vertical" className="h-4 -mx-1" />
              {Object.entries(menuHoverCardOptions)
                .filter(
                  ([type]) => value.prompt_speak_type !== 'polly' || type === 'prompt_speak_type',
                )
                .map(([type, options]) => (
                  <HoverSelect
                    key={type}
                    placeholder={labelMap[type as keyof typeof labelMap]}
                    options={options}
                    value={value[type as keyof typeof labelMap]}
                    onChange={(v: string) => handleOptionChange(type, v)}
                    popoverClassName={type === 'voice' ? 'max-w-56 w-52 p-0' : undefined}
                    align={type === 'voice' ? 'end' : undefined}
                    tooltip={`Select ${labelMap[type as keyof typeof labelMap].toLowerCase()}`}
                  />
                ))}
              <Separator orientation="vertical" className="h-4" />
              {nodeVars && (
                <NodevarList
                  nodevars={nodeVars}
                  inputElementSelector={`#audio-prompt-${promptIndex}`}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="p-3 flex gap-5 border rounded place-items-center">
            <div className="w-full">
              <audio src={value.url} controls className="w-full" />
            </div>
            <X size={24} className="cursor-pointer" onClick={() => handleOptionChange('url', '')} />
          </div>
        )}
      </div>
      {separatorPosition === 'below' && (
        <Separator orientation="horizontal" className="h-px w-full mt-4 bg-bg1" />
      )}
    </>
  )
}
